import {
    Button,
    Dialog,
    DialogBody,
    DialogFooter,
    DialogHeader,
    Input,
    Option,
    Select
} from "@material-tailwind/react";
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { AddProduct, AddProductImage } from "../services/ApiServices/ProductService";
import { AddProductSize } from "../services/ApiServices/ProductSize";
import { Materials } from "../services/Material";

type Product = {
    id: string;
    name: string;
    description: string;
    type: string;
    price: number;
    quantity: number;
    productImages: ProductImage[];
    variantTypes: any;
    createDate: string;
    status: string;
};

type ProductImage = {
    imageUrl: string;
    id: number;
};

type SizeQuantity = {
    sizeId: string;
    sizeName: string;
    quantity: number;
};

type AdminAddProductProps = {
    open: boolean;
    handleOpen: () => void;
    setLoading: (loading: boolean) => void;
    fetchProducts: () => void;
};

function AdminAddProduct({ open, handleOpen, setLoading, fetchProducts }: AdminAddProductProps) {
    const navigate = useNavigate();
    const [productInfo, setProductInfo] = useState<Product>({
        id: "",
        name: "",
        description: "",
        type: "",
        price: 0,
        quantity: 0,
        productImages: [],
        variantTypes: "",
        createDate: new Date().toISOString().slice(0, 10),
        status: "Active",
    });

    const [totalQuantity, setTotalQuantity] = useState(0);
    const [errorMessage, setErrorMessage] = useState("");
    const [productName, setProductName] = useState("");
    const [description, setDescription] = useState("");
    const [type, setType] = useState("");
    const [material, setMaterial] = useState("");
    const [price, setPrice] = useState(0);
    const [productImages, setProductImages] = useState<ProductImage[]>([]);
    const [sizeQuantities, setSizeQuantities] = useState<SizeQuantity[]>([
        { sizeId: "d9a13215-5d8c-48f4-ab1e-ed4b42340037", sizeName: "S", quantity: 0 },
        { sizeId: "a963ed3d-89bb-4e2b-924d-af91d1f12a79", sizeName: "M", quantity: 0 },
        { sizeId: "b357d697-3c93-4adb-94a7-e300566b26aa", sizeName: "L", quantity: 0 }
    ]);
    const [createDate, setCreateDate] = useState(new Date().toISOString().slice(0, 10));
    const [selectedImages, setSelectedImages] = useState<FileList | null>(null);

    const handleQuantityChange = (sizeId: string, value: number) => {
        const updatedQuantities = sizeQuantities.map(size =>
            size.sizeId === sizeId ? { ...size, quantity: value } : size
        );
        setSizeQuantities(updatedQuantities);

        const total = updatedQuantities.reduce((acc, size) => acc + size.quantity, 0);
        setTotalQuantity(total);
    };

    const addProduct = async () => {
        setLoading(true);
        try {
            const addProduct = {
                name: productName,
                description: description,
                material: material,
                type: type,
                quantity: totalQuantity,
                price: price,
                createDate: createDate || new Date().toISOString(),
                status: productInfo.status,
            };

            const product = await AddProduct(addProduct);

            for (let size of sizeQuantities) {
                if (size.quantity > 0) {
                    await AddProductSize({
                        productId: product.id,
                        sizeId: size.sizeId,
                        price: price,
                        description: description,
                        quantity: size.quantity,
                        createdAt: createDate || new Date().toISOString(),
                    });
                }
            }

            if (selectedImages) {
                const formData = new FormData();
                for (let i = 0; i < selectedImages.length; i++) {
                    formData.append('files', selectedImages[i]);
                }
                formData.append('id', product.id);

                await AddProductImage(formData);
            }

            setProductInfo(product);
            fetchProducts();
            setLoading(false);
            handleOpen();
        } catch (error: any) {
            setErrorMessage(error.response?.data?.message || 'An error occurred. Please try again later.');
            setLoading(false);
        }
    };

    const handleImagesChange = (event: any) => {
        setSelectedImages(event.target.files || null);
    };

    return (
        <Dialog placeholder={""} open={open} handler={handleOpen} className="max-w-md p-4 rounded-lg shadow-lg">
            <DialogHeader placeholder={""} className="text-[#395F18] font-bold">Thêm thông tin sản phẩm</DialogHeader>
            <DialogBody placeholder={""} className="space-y-4 max-h-96 overflow-y-auto">
                <Input crossOrigin={""} label="Ngày tạo" type="date" value={createDate} onChange={(e) => setCreateDate(e.currentTarget.value)} />
                <Input crossOrigin={""} label="Tên" value={productName} onChange={(e) => setProductName(e.currentTarget.value)} />
                <Input crossOrigin={""} label="Mô tả" value={description} onChange={(e) => setDescription(e.currentTarget.value)} />
                <Select placeholder={""} label="Loại sản phẩm" value={type} onChange={(value: any) => setType(value)}>
                    <Option value="Áo dài">Áo dài</Option>
                    <Option value="Phụ kiện">Phụ kiện</Option>
                </Select>
                <Select placeholder={""} label="Loại chất liệu" value={material} onChange={(value: any) => setMaterial(value)}>
                    {Materials.map((material: any) => (
                        <Option key={material} value={material}>{material}</Option>
                    ))}
                </Select>
                <Input crossOrigin={""} label="Giá tiền" type="number" value={price} onChange={(e) => setPrice(Number(e.currentTarget.value))} />
                {sizeQuantities.map((size) => (
                    <Input crossOrigin={""} key={size.sizeId} label={`Số lượng Size ${size.sizeName}`} type="number" value={size.quantity} onChange={(e) => handleQuantityChange(size.sizeId, Number(e.currentTarget.value))} />
                ))}
                <Input crossOrigin={""} label="Tổng Số lượng" type="number" value={totalQuantity} readOnly />
                <Input crossOrigin={""} label="Hình ảnh" type="file" multiple onChange={handleImagesChange} />
            </DialogBody>
            <DialogFooter placeholder={""} className="flex justify-end space-x-2">
                <Button placeholder={""} variant="text" color="red" onClick={handleOpen}>
                    Huỷ
                </Button>
                <Button placeholder={""} variant="gradient" color="green" onClick={addProduct}>
                    Tạo
                </Button>
            </DialogFooter>
        </Dialog>
    );
}

export default AdminAddProduct;
